import { utils } from 'ethers';
import crypto from 'crypto';

const ADDRESS_PREFIX = '41';

const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const ALPHABET_MAP: Record<string, number> = {};
for (let i = 0; i < ALPHABET.length; i++) ALPHABET_MAP[ALPHABET.charAt(i)] = i;
const BASE = 58;

export function encode58(buffer: number[]): string {
  if (buffer.length === 0) return '';
  let i: number;
  let j: number;

  const digits: number[] = [0];
  for (i = 0; i < buffer.length; i++) {
    for (j = 0; j < digits.length; j++) digits[j] <<= 8;
    digits[0] += buffer[i];
    let carry = 0;
    for (j = 0; j < digits.length; ++j) {
      digits[j] += carry;
      carry = (digits[j] / BASE) | 0;
      digits[j] %= BASE;
    }
    while (carry) {
      digits.push(carry % BASE);
      carry = (carry / BASE) | 0;
    }
  }
  for (i = 0; buffer[i] === 0 && i < buffer.length - 1; i++) digits.push(0);
  return digits
    .reverse()
    .map((digit) => ALPHABET[digit])
    .join('');
}

export function decode58(str: string): number[] {
  if (str.length === 0) return [];

  let i: number;
  let j: number;

  const bytes: number[] = [0];
  for (i = 0; i < str.length; i++) {
    const c = str[i];
    if (!(c in ALPHABET_MAP)) throw new Error('Non-base58 character');
    for (j = 0; j < bytes.length; j++) bytes[j] *= BASE;
    bytes[0] += ALPHABET_MAP[c];
    let carry = 0;
    for (j = 0; j < bytes.length; ++j) {
      bytes[j] += carry;
      carry = bytes[j] >> 8;
      bytes[j] &= 0xff;
    }
    while (carry) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  for (i = 0; str[i] === '1' && i < str.length - 1; i++) bytes.push(0);
  return bytes.reverse();
}

function decodeBase58Address(base58Sting: string): number[] {
  if (typeof base58Sting !== 'string') throw new Error('Address must be a string');
  if (base58Sting.length <= 4) throw new Error('Invalid base58 address');

  let address = decode58(base58Sting);
  if (base58Sting.length <= 4) throw new Error('Invalid base58 address');

  const len = address.length;
  const offset = len - 4;
  const checkSum = address.slice(offset);

  address = address.slice(0, offset);

  const hash0 = utils.sha256(address);
  const hash1 = utils.sha256(hash0);
  const checkSum1 = Array.from(utils.arrayify(hash1).slice(0, 4));

  if (
    checkSum[0] === checkSum1[0] &&
    checkSum[1] === checkSum1[1] &&
    checkSum[2] === checkSum1[2] &&
    checkSum[3] === checkSum1[3]
  ) {
    return address;
  }

  throw new Error('Invalid address provided');
}

export function byte2hexStr(byte: number): string {
  if (typeof byte !== 'number') throw new Error('Input must be a number');
  if (byte < 0 || byte > 255) throw new Error('Input must be a byte');

  const hexByteMap = '0123456789ABCDEF';
  let str = '';
  str += hexByteMap.charAt(byte >> 4);
  str += hexByteMap.charAt(byte & 0x0f);

  return str;
}

export function byteArray2hexStr(byteArray: number[]): string {
  let str = '';
  for (let i = 0; i < byteArray.length; i++) str += byte2hexStr(byteArray[i]);
  return str;
}

function isHex(str: string): boolean {
  return typeof str === 'string' && !isNaN(parseInt(str, 16)) && /^(0x|)[a-fA-F0-9]+$/.test(str);
}

export function toHex(address: string): string {
  if (isHex(address)) return address.toLowerCase().replace(/^0x/, ADDRESS_PREFIX);
  return byteArray2hexStr(decodeBase58Address(address)).toLowerCase();
}

export function toChecksumAddress(address: string): string {
  const normalized = address.startsWith('0x') ? address : `0x${address}`;
  return utils.getAddress(normalized);
}

export const tronAddressToEthAddress = (input: string | string[]): string | string[] => {
  let hexAddress = '';

  if (Array.isArray(input)) {
    return input.map((addr) => {
      let hexAddr = '';
      try {
        hexAddr = toChecksumAddress(toHex(addr).substring(2));
      } catch (error) {
        console.error('Error converting address in array:', {
          addr,
          hexAddr,
        });
      }
      return hexAddr;
    });
  } else if (typeof input === 'string' && input.trim()) {
    // // Split by newlines and filter out empty lines
    // const addresses = input
    //   .split('\n')
    //   .map((addr) => addr.trim())
    //   .filter((addr) => addr.length > 0);

    // const results: any[] = addresses.map((address) => {
    //   try {
    //     const convertedHex = toChecksumAddress(toHex(address).substring(2));
    //     return { input: address, output: convertedHex };
    //   } catch (err) {
    //     return {
    //       input: address,
    //       error: err instanceof Error ? err.message : String(err),
    //     };
    //   }
    // });

    try {
      hexAddress = toChecksumAddress(toHex(input).substring(2));
      return hexAddress;
    } catch (error) {
      console.error('Error converting address:', {
        input,
        hexAddress,
      });
    }

    return hexAddress;
  }

  return '';
};
